import { CloseIcon, SearchIcon } from '@c/icons';
import { Combobox } from '@headlessui/react';
import useDebounce from '@util/hooks/useDebounce';
import { isMobile } from '@util/index';
import { ProductDocument } from '@util/types/firestore/products';
import { MasterDocument } from '@util/firestore/master';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState, useRef, useCallback } from 'react';
import Avatar from './Avatar';
import Button from './Button';
import Input from './Input';
import SafeImage from './SafeImage';
import { getProductUrl } from '@util/urlHelpers';
import { pushToDataLayer } from '@util/analytics';
import { searchAlgolia } from '@util/firestore/algolia/search.service';
import { UserDocument } from '@util/types/firestore/users';

function ProductRow({ item, onResultClick }: { item: ProductDocument; onResultClick?: () => void }) {
  return (
    <Combobox.Option
      key={(item as any).objectID ?? item.id}
      value={item.title}
      className={({ active }) => cx('relative flex cursor-pointer select-none items-center py-3 pl-[2rem] pr-4', active ? 'bg-brand-secondary-light' : '')}
    >
      <Link href={getProductUrl(item.slug)} className="flex w-full items-center" onClick={onResultClick}>
        {item.thumbnail && (
          <SafeImage alt={item.title} src={item.thumbnail} style={{ objectFit: 'cover' }} height={42} width={42} className="mr-5 rounded-[1rem]" />
        )}
        <div className="truncate">{item.title}</div>
      </Link>
    </Combobox.Option>
  );
}

function MasterRow({ item, onResultClick }: { item: MasterDocument; onResultClick?: () => void }) {
  const thumb = (item as any).thumbnail ?? item.images?.[0] ?? null;
  return (
    <Combobox.Option
      key={(item as any).objectID ?? item.id}
      value={item.title}
      className={({ active }) => cx('relative flex cursor-pointer select-none items-center py-3 pl-[2rem] pr-4', active ? 'bg-brand-secondary-light' : '')}
    >
      <Link href={`/m/${item.slug}`} className="flex w-full items-center" onClick={onResultClick}>
        {thumb && (
          <SafeImage alt={item.title} src={thumb} style={{ objectFit: 'cover' }} height={42} width={42} className="mr-5 rounded-[1rem]" />
        )}
        <div className="truncate">{item.title}</div>
      </Link>
    </Combobox.Option>
  );
}

function SellerRow({ item, onResultClick }: { item: UserDocument; onResultClick?: () => void }) {
  return (
    <Combobox.Option
      key={(item as any).objectID ?? item.uid}
      value={item.username}
      className={({ active }) => cx('relative flex cursor-pointer select-none items-center py-3 pl-[2rem] pr-4', active ? 'bg-brand-secondary-light' : '')}
    >
      <Link href={`/shop-seller/${item.username_slug}`} className="flex w-full items-center gap-[1.6rem]" onClick={onResultClick}>
        <Avatar user={item} />
        <div className="flex flex-col truncate">{item.username}</div>
      </Link>
    </Combobox.Option>
  );
}

const SearchBox = ({
  onBlur,
  onFocus,
  goBack,
  onClick,
  white,
  searchIconRight,
  dismissOnBlur,
  valuationMode = false,
}: {
  onBlur?: () => void;
  onFocus?: () => void;
  goBack?: () => void;
  onClick?: () => void;
  white?: boolean;
  searchIconRight?: boolean;
  dismissOnBlur?: boolean;
  /**
   * When true, the search box shows only GearMaster model results and routes
   * directly to /m/{slug}. Product-listing rows, seller rows, and the
   * "view all / keyword" submit row are hidden. Existing usages that omit
   * this prop are completely unaffected (default false).
   */
  valuationMode?: boolean;
}) => {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState('');
  const debounced = useDebounce(searchTerm, 300);
  const [showCombobox, setShowCombobox] = useState(false);
  const [loading, setLoading] = useState(false);

  const [productHits, setProductHits] = useState<ProductDocument[]>([]);
  const [masterHits, setMasterHits] = useState<MasterDocument[]>([]);
  const [sellerHits, setSellerHits] = useState<UserDocument[]>([]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Hide results only (do NOT clear the term)
  const hideResults = useCallback(() => {
    setShowCombobox(false);
  }, []);

  // Click/Tap outside to just hide, not clear
  useEffect(() => {
    const onPointerDown = (e: PointerEvent | MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!wrapperRef.current || !target) return;
      if (!wrapperRef.current.contains(target)) hideResults();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
    };
  }, [hideResults]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const q = debounced.trim();
      if (!q) {
        setProductHits([]);
        setMasterHits([]);
        setSellerHits([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const hitsPerPage = isMobile() ? 3 : 5;

      try {
        // valuationMode renders ONLY GearMaster master results, so fetch ONLY the
        // master query and leave product/seller arrays empty. This avoids a blank
        // dropdown when a query matches a product/seller but no master. Standard
        // mode is unchanged: all three queries fire in parallel.
        if (valuationMode) {
          const masters = await searchAlgolia('GM', 'products', { query: q, page: 0, hitsPerPage });
          if (cancelled) return;

          setProductHits([]);
          setMasterHits((masters.data || []) as MasterDocument[]);
          setSellerHits([]);
        } else {
          // Exclude inactive stores: show only Pro stores, stores with live inventory,
          // or stores that listed something in the last 6 months.
          const sixMonthsAgo = Date.now() - 180 * 24 * 60 * 60 * 1000;
          const sellerActivityFilter =
            `is_gearfocus_pro = 1 OR live_listings > 0 OR last_listed > ${sixMonthsAgo} OR created > ${sixMonthsAgo}`;

          const [products, masters, sellers] = await Promise.all([
            searchAlgolia('GF', 'products', {
              query: q,
              page: 0,
              hitsPerPage,
              filters: [
                'out_of_stock:false',
                'is_verified:true',
                'is_draft:false',
                'is_expired:false'
              ]
            }),
            searchAlgolia('GM', 'products', { query: q, page: 0, hitsPerPage }),
            searchAlgolia('GF', 'sellers', {
              query: q,
              page: 0,
              hitsPerPage,
              filterString: sellerActivityFilter,
            }),
          ]);
          if (cancelled) return;

          setProductHits((products.data || []) as ProductDocument[]);
          setMasterHits((masters.data || []) as MasterDocument[]);
          setSellerHits((sellers.data || []) as UserDocument[]);
        }
      } catch {
        if (!cancelled) {
          setProductHits([]);
          setMasterHits([]);
          setSellerHits([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [debounced, valuationMode]);

  const navigate = (keyword: string) => {
    hideResults(); // don't clear term
    return router.push(`/search?view=list&keyword=${keyword}`);
  };

  const trackSearchEvent = () => {
    if (searchTerm) pushToDataLayer('search', { search_term: searchTerm });
  };

  // In valuationMode only masters are shown, so the empty-state gate must be
  // master-only; otherwise "No results" would be suppressed by product/seller
  // hits that are never rendered. Standard mode keeps the all-three sum.
  const hasResults = valuationMode
    ? (masterHits?.length ?? 0) > 0
    : (productHits?.length ?? 0) + (masterHits?.length ?? 0) + (sellerHits?.length ?? 0) > 0;

  const handleResultClick = () => {
    hideResults(); // don't clear term
  };

  return (
    <Combobox as="div" className="mx-auto w-full" onChange={(val: string) => setSearchTerm(val)}>
      {/* Wrap input + dropdown */}
      <div ref={wrapperRef} className="relative z-30">
        <Input
          id="global-search-box"
          goBack={goBack}
          searchIconRight={!!searchIconRight}
          hideSearchIcon
          onDark
          bordered
          full
          white={white}
          type="search"
          value={searchTerm}
          placeholder={valuationMode ? 'Search your camera or lens...' : 'Shop for preowned cameras and gear...'}
          trailingIconButton={
            searchTerm ? (
              <Button type="text" width="extraSmall" noXPadding onClick={() => setSearchTerm('')} leadingIcon={<CloseIcon />} />
            ) : (
              <Button
                type="text"
                width="extraSmall"
                onClick={() => {
                  trackSearchEvent();
                  // In valuationMode there is no keyword-search destination,
                  // so clicking the search icon is a no-op.
                  if (!valuationMode) navigate(encodeURIComponent(searchTerm));
                }}
                noXPadding
                leadingIcon={<SearchIcon />}
              />
            )
          }
          onClick={() => {
            if (onClick) {
              trackSearchEvent();
              onClick();
            }
          }}
          onChange={(ev) => {
            setShowCombobox(true);
            setSearchTerm(ev.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              if (valuationMode) {
                // No keyword-search destination here. Jump to the first master
                // result's page (same route as MasterRow). No-op if none yet.
                const firstMaster = masterHits?.[0];
                if (firstMaster?.slug) {
                  trackSearchEvent();
                  hideResults();
                  router.push(`/m/${firstMaster.slug}`);
                }
              } else {
                const keyword = (ev.target as HTMLInputElement).value;
                trackSearchEvent();
                navigate(encodeURIComponent(keyword));
              }
            }
            if (ev.key === 'Escape') {
              ev.stopPropagation();
              hideResults(); // hide only
            }
          }}
          onFocus={() => {
            onFocus?.();
            // If there’s a term, reopen results
            if (searchTerm.trim()) setShowCombobox(true);
          }}
          onBlur={() => {
            onBlur?.();
            if (dismissOnBlur) {
              setTimeout(() => setShowCombobox(false), 300);
            }
          }}
        />

        {showCombobox && searchTerm && (
          <Combobox.Options
            static
            className="absolute top-[4.4rem] z-10 mt-2 w-full overflow-y-auto overflow-x-hidden rounded-[1rem] bg-brand-white py-1 pb-[8rem] text-[1.4rem] shadow-xs focus:outline-hidden sm:max-h-[75vh] sm:pb-0 sm:ring-1 sm:ring-brand-black/5"
          >
            {/* "View All Results" mobile row — hidden in valuationMode (no keyword search destination) */}
            {!valuationMode && (
              <div className="flex w-full items-center justify-center font-semibold sm:hidden">
                <Button
                  type="text"
                  text="View All Results"
                  href={{ pathname: '/search', query: { keyword: searchTerm } }}
                  onClick={() => {
                    handleResultClick();
                    goBack?.();
                  }}
                />
              </div>
            )}

            {/* PRODUCTS (GF) — hidden in valuationMode */}
            {!valuationMode && productHits?.length > 0 && (
              <>
                <span className="mb-1 mt-5 block font-medium text-brand-secondary">Products</span>
                {productHits.map((p) => (
                  <ProductRow key={(p as any).objectID ?? p.id} item={p} onResultClick={handleResultClick} />
                ))}
                <div className="my-2 h-px bg-gray-100" />
              </>
            )}

            {/* MODELS / MASTER (GM) */}
            {masterHits?.length > 0 && (
              <>
                {/* Show "Models" label only in standard mode; omit category label in valuationMode */}
                {!valuationMode && (
                  <span className="mb-1 mt-5 block font-medium text-brand-secondary">Models</span>
                )}
                {masterHits.map((m) => (
                  <MasterRow key={(m as any).objectID ?? m.id} item={m} onResultClick={handleResultClick} />
                ))}
                {!valuationMode && <div className="my-2 h-px bg-gray-100" />}
              </>
            )}

            {/* SELLERS (GF) — hidden in valuationMode */}
            {!valuationMode && sellerHits?.length > 0 && (
              <>
                <span className="mb-1 mt-5 block font-medium text-brand-secondary">Sellers</span>
                {sellerHits.map((s) => (
                  <SellerRow key={(s as any).objectID ?? s.uid} item={s} onResultClick={handleResultClick} />
                ))}
              </>
            )}

            {loading && <div className="px-4 py-3 text-sm text-gray-500">Searching…</div>}
            {!loading && !hasResults && <div className="px-4 py-3 text-sm text-gray-500">No results</div>}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

export default SearchBox;
